import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, MapPin, Bed, Bath, Maximize, Toilet, Building2, BarChart2, Shield, Award, ChevronRight, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react'
import { motion } from 'framer-motion'
import ChatWidget from '@/components/ChatWidget'
import PropertyFilters, { FILTERS_INITIAL_STATE, PropertyFiltersState, applyFilters } from '@/components/PropertyFilters'
import PropertyPagination from '@/components/PropertyPagination'

interface Property {
  id: string
  seo_title: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  purpose: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  area_m2?: number
  zone: string
  city?: string
  amenities?: string[]
  featured_image?: string
}

interface CorporativoTemplateProps {
  agent: {
    name: string
    bio: string
    phone: string
    specialties: string[]
    zones: string[]
    avatar_url?: string
    social_media?: {
      facebook?: string
      instagram?: string
      linkedin?: string
      tiktok?: string
      youtube?: string
    }
  }
}

const CorporativoTemplate: React.FC<CorporativoTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()
  const [properties, setProperties] = useState<Property[]>([])
  const [filters, setFilters] = useState<PropertyFiltersState>(FILTERS_INITIAL_STATE)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(6)

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const subdomain = window.location.hostname.split('.')[0]
        const response = await fetch(`/api/agents/subdomain/${subdomain}/properties`)
        if (response.ok) {
          const data = await response.json()
          setProperties(data)
        }
      } catch (error) {
        console.error('Error fetching properties:', error)
      }
    }
    fetchProperties()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  const formatPrice = (price: number, currency: string) => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`
  }

  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  const filteredProperties = applyFilters(properties, filters)
  const propertyTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))]
  const totalPages = perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties = perPage === 0
    ? filteredProperties
    : filteredProperties.slice((currentPage - 1) * perPage, currentPage * perPage)
  const showPhone = false
  const showWhatsapp = false

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>

      {/* Header corporativo */}
      <header className="bg-[#0F1F3D] border-b-4 border-[#C8A84B] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center justify-between py-4">
            {/* Logo/Nombre */}
            <div className="flex items-center gap-4">
              {agent.avatar_url ? (
                <img src={agent.avatar_url} alt={agent.name}
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-[#C8A84B]" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#C8A84B] flex items-center justify-center">
                  <span className="text-[#0F1F3D] font-bold text-lg">{agent.name.charAt(0)}</span>
                </div>
              )}
              <div>
                <div className="text-white font-bold text-lg leading-tight">{agent.name}</div>
                <div className="text-[#C8A84B] text-xs tracking-widest uppercase">Asesor Inmobiliario</div>
              </div>
            </div>
            {/* Contacto header */}
            <div className="hidden md:flex items-center gap-6">
              {showPhone && agent.phone && (
                <a href={`tel:${agent.phone}`}
                  className="flex items-center gap-2 text-white hover:text-[#C8A84B] transition-colors text-sm">
                  <Phone size={16} />
                  {agent.phone}
                </a>
              )}
              {showWhatsapp && agent.phone && (
                <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-[#C8A84B] text-[#0F1F3D] font-bold text-sm rounded transition-all hover:bg-[#B8943B]">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero corporativo */}
      <section className="bg-[#0F1F3D] py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Texto */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Línea dorada */}
              <div className="flex items-center gap-4">
                <div className="h-1 w-12 bg-[#C8A84B]" />
                <span className="text-[#C8A84B] text-xs tracking-[0.3em] uppercase font-medium">
                  Soluciones Inmobiliarias Corporativas
                </span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                {agent.name}
              </h1>

              {agent.bio && (
                <p className="text-lg text-slate-300 leading-relaxed">
                  {agent.bio}
                </p>
              )}

              {/* KPIs */}
              <div className="grid grid-cols-3 gap-6 py-6 border-y border-white/10">
                {[
                  { value: properties.length, label: 'Propiedades' },
                  { value: agent.zones?.length || 0, label: 'Zonas' },
                  { value: agent.specialties?.length || 0, label: 'Especialidades' },
                ].map(({ value, label }, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl font-bold text-[#C8A84B]">{value}</div>
                    <div className="text-slate-400 text-xs uppercase tracking-wider mt-1">{label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                {(showPhone || showWhatsapp) && agent.phone && (
                  <>
                    {showPhone && (
                      <a href={`tel:${agent.phone}`}
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-[#C8A84B] hover:bg-[#B8943B] text-[#0F1F3D] font-bold rounded transition-all hover:scale-105 shadow-lg">
                        <Phone size={20} />
                        {agent.phone}
                      </a>
                    )}
                    {showWhatsapp && (
                      <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] hover:bg-[#1fb855] text-white font-bold rounded transition-all hover:scale-105 shadow-lg">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp
                      </a>
                    )}
                  </>
                )}
              </div>

              {/* Social */}
              {agent.social_media && Object.values(agent.social_media).some(v => v) && (
                <div className="flex gap-3">
                  {agent.social_media.facebook && (
                    <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-[#C8A84B] transition-colors">
                      <Facebook size={18} />
                    </a>
                  )}
                  {agent.social_media.instagram && (
                    <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-[#C8A84B] transition-colors">
                      <Instagram size={18} />
                    </a>
                  )}
                  {agent.social_media.linkedin && (
                    <a href={agent.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-[#C8A84B] transition-colors">
                      <Linkedin size={18} />
                    </a>
                  )}
                  {agent.social_media.tiktok && (
                    <a href={agent.social_media.tiktok} target="_blank" rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-[#C8A84B] transition-colors">
                      <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </a>
                  )}
                  {agent.social_media.youtube && (
                    <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                      className="p-2 text-slate-400 hover:text-[#C8A84B] transition-colors">
                      <Youtube size={18} />
                    </a>
                  )}
                </div>
              )}
            </motion.div>

            {/* Pilares corporativos */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="space-y-4"
            >
              {[
                { icon: Shield, title: 'Seguridad Jurídica', desc: 'Cada operación respaldada con asesoría legal especializada y documentación completa.' },
                { icon: BarChart2, title: 'Análisis de Mercado', desc: 'Valuaciones precisas basadas en datos reales del mercado inmobiliario actual.' },
                { icon: Award, title: 'Trayectoria Comprobada', desc: 'Años de experiencia en el sector con resultados medibles y clientes satisfechos.' },
                { icon: Building2, title: 'Portafolio Diversificado', desc: 'Oficinas, locales comerciales, naves industriales y propiedades residenciales.' },
              ].map(({ icon: Icon, title, desc }, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="flex gap-4 p-5 bg-white/5 border border-white/10 rounded hover:border-[#C8A84B]/40 transition-colors group"
                >
                  <div className="w-10 h-10 bg-[#C8A84B]/10 rounded flex items-center justify-center flex-shrink-0 group-hover:bg-[#C8A84B]/20 transition-colors">
                    <Icon size={20} className="text-[#C8A84B]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{title}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Propiedades */}
      {properties.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-8">

            {/* Encabezado sección */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-4"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="h-1 w-8 bg-[#C8A84B]" />
                <span className="text-[#C8A84B] text-xs tracking-widest uppercase font-medium">Inventario</span>
              </div>
              <div className="flex items-end justify-between">
                <h2 className="text-3xl font-bold text-[#0F1F3D]">Portafolio de Propiedades</h2>
              </div>

              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                propertyTypes={propertyTypes}
                totalProperties={properties.length}
                filteredCount={filteredProperties.length}
                colorSchemeKey="navy"
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(v => !v)}
              />
            </motion.div>

            {/* Grid propiedades — estilo tabla corporativa */}
            <div className="space-y-3 mt-10">
              {/* Header tabla */}
              <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 bg-[#0F1F3D] text-[#C8A84B] text-xs font-bold uppercase tracking-wider rounded">
                <div className="col-span-4">Propiedad</div>
                <div className="col-span-2">Tipo / Propósito</div>
                <div className="col-span-2">Ubicación</div>
                <div className="col-span-2">Características</div>
                <div className="col-span-2 text-right">Precio</div>
              </div>

              {paginatedProperties.map((property, index) => (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  viewport={{ once: true }}
                  onClick={() => handlePropertyClick(property.seo_title)}
                  className="group cursor-pointer bg-white border border-slate-200 hover:border-[#C8A84B] rounded transition-all duration-300 hover:shadow-md overflow-hidden"
                >
                  <div className="lg:grid lg:grid-cols-12 gap-4 items-center">
                    {/* Imagen + título */}
                    <div className="col-span-4 flex items-center gap-4 p-4">
                      <div className="w-20 h-16 flex-shrink-0 overflow-hidden rounded bg-slate-100">
                        {property.featured_image ? (
                          <img src={property.featured_image} alt={property.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-slate-100">
                            <Building2 size={24} className="text-slate-300" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-[#0F1F3D] group-hover:text-[#C8A84B] transition-colors text-sm leading-tight truncate">
                          {property.title}
                        </h3>
                        {property.description && (
                          <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                            {property.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Tipo */}
                    <div className="col-span-2 px-4 lg:px-0 pb-2 lg:pb-0">
                      <span className="inline-block px-2 py-1 bg-[#0F1F3D]/5 text-[#0F1F3D] text-xs font-medium rounded">
                        {property.property_type || 'N/A'}
                      </span>
                      <span className="block text-xs text-slate-400 mt-1 capitalize">{property.purpose}</span>
                    </div>

                    {/* Ubicación */}
                    <div className="col-span-2 px-4 lg:px-0 pb-2 lg:pb-0">
                      <div className="flex items-center gap-1 text-slate-600 text-xs">
                        <MapPin size={12} className="text-[#C8A84B] flex-shrink-0" />
                        <span className="truncate">{property.zone}</span>
                      </div>
                      {property.city && (
                        <span className="text-xs text-slate-400 ml-4">{property.city}</span>
                      )}
                    </div>

                    {/* Características */}
                    <div className="col-span-2 px-4 lg:px-0 pb-2 lg:pb-0">
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        {property.bedrooms && (
                          <span className="flex items-center gap-1"><Bed size={12} /> {property.bedrooms}</span>
                        )}
                        {property.bathrooms && (
                          <span className="flex items-center gap-1"><Bath size={12} /> {property.bathrooms}</span>
                        )}
                        {property.half_bathrooms > 0 && (
                          <span className="flex items-center gap-1"><Toilet size={12} /> {property.half_bathrooms}</span>
                        )}
                        {property.area_m2 && (
                          <span className="flex items-center gap-1"><Maximize size={12} /> {property.area_m2}m²</span>
                        )}
                      </div>
                    </div>

                    {/* Precio */}
                    <div className="col-span-2 px-4 lg:px-0 pb-4 lg:pb-0 flex items-center justify-between lg:justify-end gap-3">
                      <span className="font-bold text-[#0F1F3D] text-sm">
                        {formatPrice(property.price, property.currency)}
                      </span>
                      <ChevronRight size={16} className="text-slate-300 group-hover:text-[#C8A84B] transition-colors flex-shrink-0" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <PropertyPagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={setPerPage}
              colorSchemeKey="navy"
            />
          </div>
        </section>
      )}

      {/* Especialidades */}
      {agent.specialties?.length > 0 && (
        <section className="py-20 bg-[#0F1F3D]">
          <div className="max-w-7xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-2">
                <div className="h-1 w-8 bg-[#C8A84B]" />
                <span className="text-[#C8A84B] text-xs tracking-widest uppercase font-medium">Servicios</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-12">Especialidades</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {agent.specialties.map((specialty, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.07 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 p-4 border border-white/10 hover:border-[#C8A84B]/50 rounded transition-colors group"
                  >
                    <div className="w-2 h-2 rounded-full bg-[#C8A84B] flex-shrink-0" />
                    <span className="text-slate-300 group-hover:text-white transition-colors capitalize text-sm">
                      {specialty}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Zonas */}
      {agent.zones?.length > 0 && (
        <section className="py-20 bg-slate-100">
          <div className="max-w-7xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-4 mb-2">
                <div className="h-1 w-8 bg-[#C8A84B]" />
                <span className="text-[#C8A84B] text-xs tracking-widest uppercase font-medium">Cobertura</span>
              </div>
              <h2 className="text-3xl font-bold text-[#0F1F3D] mb-10">Zonas de Operación</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {agent.zones.map((zone, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3 p-4 bg-white border border-slate-200 hover:border-[#C8A84B] rounded transition-all group"
                  >
                    <MapPin size={16} className="text-[#C8A84B] flex-shrink-0" />
                    <span className="text-[#0F1F3D] font-medium text-sm group-hover:text-[#C8A84B] transition-colors">
                      {zone}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Footer corporativo */}
      <footer className="bg-[#0F1F3D] border-t-4 border-[#C8A84B] py-12">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-1 w-8 bg-[#C8A84B]" />
                <span className="text-[#C8A84B] text-xs tracking-widest uppercase">Contacto Directo</span>
              </div>
              <h2 className="text-3xl font-bold text-white">
                ¿Listo para su próxima operación?
              </h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                Asesoría profesional y discreta para inversores, empresas y particulares exigentes.
              </p>
            </div>
            <div className="space-y-4">
              {(showPhone || showWhatsapp) && agent.phone && (
                <>
                  {showPhone && (
                    <a href={`tel:${agent.phone}`}
                      className="flex items-center justify-between w-full p-4 bg-white/5 hover:bg-[#C8A84B]/10 border border-white/10 hover:border-[#C8A84B]/50 rounded transition-all group">
                      <div className="flex items-center gap-3">
                        <Phone size={18} className="text-[#C8A84B]" />
                        <div>
                          <div className="text-white text-sm font-semibold">Llamada directa</div>
                          <div className="text-slate-400 text-xs">{agent.phone}</div>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-slate-400 group-hover:text-[#C8A84B] transition-colors" />
                    </a>
                  )}
                  {showWhatsapp && (
                    <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-between w-full p-4 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 hover:border-[#25D366] rounded transition-all group">
                      <div className="flex items-center gap-3">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px] text-[#25D366]">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        <div>
                          <div className="text-white text-sm font-semibold">WhatsApp Business</div>
                          <div className="text-slate-400 text-xs">Respuesta en menos de 1 hora</div>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-slate-400 group-hover:text-[#25D366] transition-colors" />
                    </a>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-slate-500 text-xs">
              © {new Date().getFullYear()} {agent.name} · Todos los derechos reservados
            </span>
            {agent.social_media && Object.values(agent.social_media).some(v => v) && (
              <div className="flex gap-4">
                {agent.social_media.facebook && (
                  <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                    className="text-slate-500 hover:text-[#C8A84B] transition-colors"><Facebook size={16} /></a>
                )}
                {agent.social_media.instagram && (
                  <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                    className="text-slate-500 hover:text-[#C8A84B] transition-colors"><Instagram size={16} /></a>
                )}
                {agent.social_media.linkedin && (
                  <a href={agent.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                    className="text-slate-500 hover:text-[#C8A84B] transition-colors"><Linkedin size={16} /></a>
                )}
                {agent.social_media.tiktok && (
                  <a href={agent.social_media.tiktok} target="_blank" rel="noopener noreferrer"
                    className="text-slate-500 hover:text-[#C8A84B] transition-colors">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                )}
                {agent.social_media.youtube && (
                  <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                    className="text-slate-500 hover:text-[#C8A84B] transition-colors"><Youtube size={16} /></a>
                )}
              </div>
            )}
          </div>
        </div>
      </footer>

      <ChatWidget
        agentName={agent.name}
        agentSubdomain={window.location.hostname.split('.')[0]}
        agentAvatar={agent.avatar_url}
        agentPlan={agent.plan}
      />
    </div>
  )
}

export default CorporativoTemplate
